// ─── Game orchestrator ──────────────────────────────────────────────────────
// Owns the state engine, the renderer, the audio, the rAF loop, and the token
// physics + input. Emits high-level events so main.ts can wire UI / analytics /
// score submission without knowing about frames.

import { LANE_COUNT, type Chain, type Color } from '@gametypes';
import { isOnBeat } from '@game/audioSync';
import { GameEngine } from './state';
import { Renderer, type FallingToken } from './render';
import { AudioEngine } from '../audio';
import {
  ITEM_SPEED,
  DIFFICULTY_RAMP_MS,
  DIFFICULTY_MAX_MULTIPLIER,
  ON_BEAT_WINDOW_MS,
  DEFAULT_BPM,
} from '../config';

export interface GameEvents {
  onTokenPlaced: (lane: number) => void;
  onChainCleared: (chain: Chain, combo: number, wasOnBeat: boolean) => void;
  onBeatBonus: (combo: number) => void;
  onMaxCombo: (maxCombo: number) => void;
  onOnboardingChain: (moves: number) => void;
  onScoreChanged: (score: number, combo: number) => void;
  onGameOver: () => void;
}

export class Game {
  readonly engine = new GameEngine();
  readonly renderer: Renderer;
  readonly audio: AudioEngine;
  private events: GameEvents;
  private token: FallingToken | null = null;
  private raf = 0;
  private lastT = 0;
  private bpm = DEFAULT_BPM;
  private running = false;

  constructor(canvas: HTMLCanvasElement, audio: AudioEngine, events: GameEvents) {
    this.renderer = new Renderer(canvas);
    this.audio = audio;
    this.events = events;
    this.renderer.resize();

    window.addEventListener('resize', this.handleResize);
    canvas.addEventListener('pointerdown', this.handlePointer);
  }

  private handleResize = (): void => {
    this.renderer.resize();
    if (this.token) this.token.x = this.renderer.laneCenterX(this.token.targetLane);
  };

  private handlePointer = (e: PointerEvent): void => {
    if (this.engine.status !== 'playing' || !this.token) return;
    e.preventDefault();
    const rect = this.renderer.canvas.getBoundingClientRect();
    const lane = this.renderer.laneFromX(e.clientX - rect.left);
    if (lane === this.token.targetLane) {
      // hard drop — the on-beat timing lever (mirrors GameBoard hardDropRequested)
      this.land(true);
    } else {
      this.retarget(lane);
    }
  };

  /** Point the falling token at `lane`, auto-redirecting away from a full lane. */
  private retarget(lane: number): void {
    if (!this.token) return;
    let target = lane;
    if (this.engine.landingRow(target) === -1) {
      const open = this.nearestOpenLane(lane);
      if (open === -1) return;
      target = open;
    }
    this.token.targetLane = target;
  }

  private nearestOpenLane(from: number): number {
    for (let d = 0; d < LANE_COUNT; d++) {
      for (const l of [from - d, from + d]) {
        if (l >= 0 && l < LANE_COUNT && this.engine.landingRow(l) !== -1) return l;
      }
    }
    return -1;
  }

  start(seed: string, bpm: number = DEFAULT_BPM): void {
    this.engine.startGame(seed);
    this.bpm = bpm;
    this.spawnToken();
    this.events.onScoreChanged(0, 1);
    this.wake();
  }

  private wake(): void {
    if (this.running) return;
    this.running = true;
    this.lastT = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  pause(): void {
    this.engine.pause();
    this.audio.pause();
  }

  resume(): void {
    this.engine.resume();
    this.audio.resume();
    this.lastT = performance.now();
    this.wake();
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.token = null;
    this.engine.reset();
    this.audio.stop();
  }

  destroy(): void {
    this.stop();
    this.renderer.dispose();
    window.removeEventListener('resize', this.handleResize);
    this.renderer.canvas.removeEventListener('pointerdown', this.handlePointer);
  }

  private spawnToken(): void {
    const color = this.engine.currentColor;
    if (!color) {
      this.token = null;
      return;
    }
    // default target: an open lane nearest the middle
    let target = Math.floor(LANE_COUNT / 2);
    if (this.engine.landingRow(target) === -1) target = this.nearestOpenLane(target);
    this.token = {
      color: color as Color,
      x: this.renderer.laneCenterX(Math.max(0, target)),
      y: 0,
      targetLane: Math.max(0, target),
    };
  }

  private currentSpeed(): number {
    const elapsed = this.engine.startedAt ? Date.now() - this.engine.startedAt : 0;
    const mult = Math.min(1 + elapsed / DIFFICULTY_RAMP_MS, DIFFICULTY_MAX_MULTIPLIER);
    return ITEM_SPEED * mult;
  }

  private land(hardDrop: boolean): void {
    if (!this.token) return;
    const lane = this.token.targetLane;
    if (this.engine.landingRow(lane) === -1) {
      const open = this.nearestOpenLane(lane);
      if (open === -1) {
        // board genuinely full — force the drop so the engine sets gameover
        this.commitDrop(lane);
        return;
      }
      this.token.targetLane = open;
      if (hardDrop) this.commitDrop(open);
      return;
    }
    this.commitDrop(lane);
  }

  private commitDrop(lane: number): void {
    const pos = this.audio.positionMs();
    const wasOnBeat = pos > 0 && isOnBeat(pos, this.bpm, ON_BEAT_WINDOW_MS);
    const before = this.engine.maxCombo;
    const res = this.engine.dropItem(lane, wasOnBeat);
    if (!res.landed && !res.gameOver) return;

    this.audio.playSfx('place');
    this.events.onTokenPlaced(lane);

    if (res.chains.length > 0) {
      this.renderer.burst(res.chains);
      this.audio.playSfx(wasOnBeat ? 'onbeat' : 'chain');
      for (const c of res.chains) {
        this.events.onChainCleared(c, res.comboAfter, wasOnBeat);
      }
      if (wasOnBeat) this.events.onBeatBonus(res.comboAfter);
      else this.renderer.flashLane(lane); // near-miss: cleared, but missed the bonus
      if (res.firstOnboardingChain) this.events.onOnboardingChain(this.engine.totalMoves);
    }

    if (this.engine.maxCombo > before) this.events.onMaxCombo(this.engine.maxCombo);
    this.events.onScoreChanged(this.engine.score, this.engine.combo);

    if (res.gameOver) {
      this.audio.playSfx('gameover');
      this.token = null;
      this.events.onGameOver();
      return;
    }
    this.spawnToken();
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    const dt = Math.min(0.05, (now - this.lastT) / 1000);
    this.lastT = now;

    // keep animating while playing, or while effects are still settling
    const keepGoing = this.engine.status === 'playing' || this.renderer.hasActiveEffects();
    if (keepGoing) this.raf = requestAnimationFrame(this.frame);
    else this.running = false;

    if (this.engine.status === 'playing' && this.token) {
      const t = this.token;
      const targetX = this.renderer.laneCenterX(t.targetLane);
      t.x += (targetX - t.x) * Math.min(1, dt * 14);
      t.y += this.currentSpeed() * dt;
      const landRow = this.engine.landingRow(t.targetLane);
      const landY =
        landRow === -1 ? this.renderer.cssH + 999 : this.renderer.rowCenterY(landRow);
      if (t.y >= landY) {
        t.y = landY;
        this.land(false);
      }
    }

    const shownToken = this.engine.status === 'playing' ? this.token : null;
    const hint =
      this.engine.isOnboarding && this.engine.status === 'playing' && this.engine.totalMoves < 3
        ? this.engine.totalMoves === 0
          ? 'tap a lane to aim'
          : 'tap the same lane again to drop'
        : null;
    this.renderer.draw({
      grid: this.engine.grid,
      token: shownToken,
      targetLandingRow: shownToken ? this.engine.landingRow(shownToken.targetLane) : -1,
      dt,
      beatPosMs: this.audio.positionMs(),
      bpm: this.bpm,
      queue: this.engine.queue,
      hint,
    });
  };
}
